/**
 * StudioStartScreen — start-zone for an empty campaign in the Studio.
 *
 * Three doors:
 *   - "create" door starts by hand: the first beat, selected, with its name and
 *     prep text in the overview (onCreateBeat — the same path as «+ Beat»)
 *   - "tell" door opens the AI column (onTell)
 *   - "load" door reveals the text-import flow driven by useCampaignTextImport
 *
 * Once the load flow is revealed, this component routes on textImport.step
 * through the existing campaign-import review steps (which must NOT be edited)
 * and the T003 StudioBulkReview wrapper. Relations/apply transitions are
 * auto-triggered following the ImportWizard pattern.
 *
 * After a successful import (step === 'done') an E5 banner offers to create
 * map pages + generate art for locations that have a description but no page.
 * Generation failures surface as toasts and never block the result view.
 */

import { apiFetch } from '../../lib/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { JSX } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { TranslationKeys } from '@/i18n/types.js';
import type {
  CatalogEntry,
  DetailedEntity,
  CampaignRelation,
  WaveApplyResult,
} from '@roll1/shared';
import { StudioBulkReview } from './StudioBulkReview.js';
import { TextInputStep } from '../campaign-import/TextInputStep.js';
import { DetailReviewStep } from '../campaign-import/DetailReviewStep.js';
import { RelationsReviewStep } from '../campaign-import/RelationsReviewStep.js';
import { ApplyProgressStep } from '../campaign-import/ApplyProgressStep.js';
import { ImportResultStep } from '../campaign-import/ImportResultStep.js';

/** Return shape of useCampaignTextImport (mirrors the hook contract). */
export interface StudioTextImport {
  step:
    | 'idle'
    | 'cataloging'
    | 'catalog-review'
    | 'detailing'
    | 'detail-review'
    | 'extracting-relations'
    | 'relations-review'
    | 'applying'
    | 'done'
    | 'error';
  error: string | null;
  isLoading: boolean;
  text: string;
  catalog: CatalogEntry[];
  details: DetailedEntity[];
  detailProgress: { current: number; total: number } | null;
  relations: CampaignRelation[];
  waveResults: WaveApplyResult[];
  startCatalog: (text: string) => Promise<void>;
  confirmCatalog: (entries: CatalogEntry[]) => Promise<void>;
  confirmDetails: (details: DetailedEntity[]) => void;
  startRelations: () => Promise<void>;
  confirmRelations: (relations: CampaignRelation[]) => void;
  startApply: () => Promise<void>;
  skipRelations: () => void;
  stop: () => void;
  reset: () => void;
}

/** Per-wave status shape consumed by ApplyProgressStep. */
interface WaveStatus {
  wave: number;
  label: string;
  status: 'pending' | 'running' | 'done' | 'error';
  result?: WaveApplyResult;
}

export interface StudioStartScreenProps {
  campaignId: string;
  /** The GM (the map route resolves the member by it). */
  participantId?: string;
  /** Opens the AI column (setAiColumnOpen(true)). */
  onTell: () => void;
  /** Creates the first beat by hand (the plan's «+ Beat» path). */
  onCreateBeat?: () => void;
  /** A beat create is in flight — the door waits for its answer. */
  creatingBeat?: boolean;
  textImport: StudioTextImport;
  t: (key: keyof TranslationKeys) => string;
}

/** Replace named placeholders like {count} in a raw translation string. */
function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
  );
}

/** Locations carrying a usable description but no page yet (E5 candidates). */
function mapCandidatesFrom(details: DetailedEntity[]): DetailedEntity[] {
  return details.filter(
    (d): d is DetailedEntity =>
      d.entityType === 'location' &&
      typeof (d as { description?: unknown }).description === 'string' &&
      (d as { description: string }).description.trim().length > 0 &&
      !('pageId' in d && Boolean((d as { pageId?: unknown }).pageId)),
  );
}

/** Build wave rows for ApplyProgressStep from raw results + running flag. */
function toWaveStatuses(waveResults: WaveApplyResult[], isRunning: boolean): WaveStatus[] {
  if (waveResults.length > 0) {
    return waveResults.map((result) => ({
      wave: result.wave,
      label: result.label,
      status: result.failed > 0 ? 'error' : 'done',
      result,
    }));
  }
  return [
    { wave: 1, label: 'Wave 1', status: isRunning ? 'running' : 'pending' },
    { wave: 2, label: 'Wave 2', status: 'pending' },
    { wave: 3, label: 'Wave 3', status: 'pending' },
  ];
}

export function StudioStartScreen({
  campaignId,
  participantId = '',
  onTell,
  onCreateBeat,
  creatingBeat = false,
  textImport,
  t,
}: StudioStartScreenProps): JSX.Element {
  const [loadRevealed, setLoadRevealed] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [creatingMaps, setCreatingMaps] = useState(false);

  const { step } = textImport;

  // Auto-trigger relations extraction (ImportWizard pattern).
  useEffect(() => {
    if (step === 'extracting-relations') {
      void textImport.startRelations();
    }
  }, [step, textImport]);

  // Auto-trigger apply once relations are confirmed / skipped.
  useEffect(() => {
    if (
      step === 'applying' &&
      !textImport.isLoading &&
      textImport.waveResults.length === 0
    ) {
      void textImport.startApply();
    }
  }, [step, textImport]);

  const mapCandidates = useMemo(
    () => mapCandidatesFrom(textImport.details),
    [textImport.details],
  );

  const handleCreateMaps = useCallback(async () => {
    setCreatingMaps(true);
    const targets = mapCandidates.slice(0, 3);
    for (const location of targets) {
      const description =
        (location as { description?: string }).description ?? location.name;
      const pageId = (location as { pageId?: string }).pageId;
      if (!pageId) {
        // No page reference available for this location — skip generation but
        // keep going; missing pages are a non-blocking condition.
        continue;
      }
      try {
        // The route needs the member (requireCampaignMember); without it every call was a 400 (group Q facts).
        const res = await apiFetch(`/api/generate-map?sessionId=${encodeURIComponent(campaignId)}&participantId=${encodeURIComponent(participantId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            prompt: description.slice(0, 1000),
            pageId,
          }),
        });
        if (!res.ok) {
          throw new Error(res.statusText);
        }
      } catch {
        toast.error(interpolate(t('studio.start.maps.action'), { name: location.name }));
      }
    }
    setCreatingMaps(false);
    setBannerDismissed(true);
  }, [campaignId, participantId, mapCandidates, t]);

  // ---- Doors (initial view) ----------------------------------------------
  if (!loadRevealed && step === 'idle') {
    return (
      <div data-testid="studio-start" className="flex flex-col gap-4 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {onCreateBeat !== undefined ? (
            <button
              type="button"
              data-testid="studio-door-create-beat"
              onClick={onCreateBeat}
              disabled={creatingBeat}
              className="flex flex-col gap-2 rounded-md border p-6 text-left transition-colors hover:bg-muted disabled:cursor-wait disabled:opacity-60"
            >
              <span className="text-title font-semibold">{t('studio.start.first.title')}</span>
              <span className="text-ui text-muted-foreground">{t('studio.start.first.desc')}</span>
            </button>
          ) : null}
          <button
            type="button"
            data-testid="studio-door-tell"
            onClick={onTell}
            className="flex flex-col gap-2 rounded-md border p-6 text-left transition-colors hover:bg-muted"
          >
            <span className="text-title font-semibold">{t('studio.start.tell.title')}</span>
            <span className="text-ui text-muted-foreground">{t('studio.start.tell.desc')}</span>
          </button>
          <button
            type="button"
            data-testid="studio-door-load"
            onClick={() => setLoadRevealed(true)}
            className="flex flex-col gap-2 rounded-md border p-6 text-left transition-colors hover:bg-muted"
          >
            <span className="text-title font-semibold">{t('studio.start.load.title')}</span>
            <span className="text-ui text-muted-foreground">{t('studio.start.load.desc')}</span>
          </button>
        </div>
      </div>
    );
  }

  const backToDoors = () => {
    textImport.reset();
    setLoadRevealed(false);
    setBannerDismissed(false);
  };

  // ---- Load flow (routed by step) ----------------------------------------
  return (
    <div data-testid="studio-start" className="flex flex-col gap-4 p-6">
      {(step === 'idle' || step === 'cataloging') && (
        <TextInputStep
          onSubmit={(inputText) => void textImport.startCatalog(inputText)}
          onBack={backToDoors}
          onCancel={backToDoors}
          isLoading={step === 'cataloging' || textImport.isLoading}
        />
      )}

      {step === 'catalog-review' && (
        <StudioBulkReview
          entries={textImport.catalog}
          onConfirm={(selected) => void textImport.confirmCatalog(selected)}
          onReject={backToDoors}
          onBack={backToDoors}
          onCancel={backToDoors}
        />
      )}

      {(step === 'detailing' || step === 'detail-review') && (
        <DetailReviewStep
          details={textImport.details}
          progress={textImport.detailProgress ?? undefined}
          isLoading={step === 'detailing' || textImport.isLoading}
          onConfirm={(selected) => textImport.confirmDetails(selected)}
          onCancel={backToDoors}
        />
      )}

      {(step === 'extracting-relations' || step === 'relations-review') && (
        <RelationsReviewStep
          relations={textImport.relations}
          catalogEntries={textImport.catalog}
          onConfirm={(rels) => {
            textImport.confirmRelations(rels);
            void textImport.startApply();
          }}
          onSkip={() => textImport.skipRelations()}
          onCancel={backToDoors}
          isLoading={step === 'extracting-relations' || textImport.isLoading}
        />
      )}

      {step === 'applying' && (
        <ApplyProgressStep
          waves={toWaveStatuses(textImport.waveResults, textImport.isLoading)}
          onStop={() => textImport.stop()}
          onCancel={backToDoors}
          isRunning={textImport.isLoading}
        />
      )}

      {step === 'done' && (
        <div className="flex flex-col gap-4">
          {mapCandidates.length > 0 && !bannerDismissed && (
            <div
              data-testid="studio-start-maps-banner"
              className="flex items-center justify-between gap-3 rounded-md border bg-muted/50 p-4"
            >
              <span className="text-ui">
                {interpolate(t('studio.start.maps.banner'), { count: mapCandidates.length })}
              </span>
              <Button
                size="sm"
                disabled={creatingMaps}
                onClick={() => void handleCreateMaps()}
              >
                {t('studio.start.maps.action')}
              </Button>
            </div>
          )}
          <ImportResultStep
            waveResults={textImport.waveResults}
            onClose={backToDoors}
          />
        </div>
      )}

      {step === 'error' && (
        <div className="flex flex-col gap-4">
          <p className="text-ui text-destructive">{textImport.error}</p>
          <div className="flex justify-end">
            <Button variant="outline" onClick={backToDoors}>
              {t('studio.start.load.title')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
